import { JssFormData } from '@sitecore-jss/sitecore-jss-forms/types/JssFormData';
import { FormResult } from '@sitecore-jss/sitecore-jss-forms/types/FormResult';
import { Form } from '@sitecore-jss/sitecore-jss-react-forms';
import React, { FC, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { EventEnum } from '../../enums/event.enum';
import { sitecoreApiHost, sitecoreApiKey, defaultLanguage } from '../../temp/config.js';
import { isInternalUrl } from '../../utils/link.utils';
import { windowService } from '../../utils/window.utils';
import FieldValidationError from './components/fieldValidationError';
import fieldWrapper from './components/fieldWrapper';
import fieldFactory from './fieldFactory';
import { TSitecoreJssFormProps } from './types/sitecoreJssForm.types';
import { googleDataLayerPush } from '../../utils/analytics';
import CustomFormContextProvider from './components/customFormContextProvider';
import customFieldTypes from './types/field.types';
import useSitecoreContext from '../../hooks/useSitecoreContext';
import { APIPrefix } from '../../constants/api.constants';
import ThankYou from './components/thankYou';
import { TDropdownAnalyticsSendData } from './components/productSelector/dropdownContext';
import './styles.scss'

function withRouter(Component) {
  function ComponentWithRouterProp(props) {
    let location = useLocation();
    let navigate = useNavigate();
    let params = useParams();
    return (
      <Component
        {...props}
        router={{ location, navigate, params }}
      />
    );
  }

  return ComponentWithRouterProp;
}

const ErrorComponent = (): JSX.Element => <></>;

const SitecoreJssForm: FC<TSitecoreJssFormProps> = (props) => {
  const { fields, router , params} = props;
  const [productSelectorItems, setProductSelectorItems] = useState<TDropdownAnalyticsSendData[]>([]);
  const thankyouMessageId = params?.thankyouMessageSetting || '';
  const [showThankyouMessage, updateShowThankyouMessage] = useState<boolean>(false);
  const [thankYouMessageFields, updateThankYouMessageFields] = useState<any>(undefined);
  const { sitecoreContext } = useSitecoreContext();
  const language = sitecoreContext.language;

  const pushDataToAnalytics = (formData: JssFormData) => {
    const formNameValue = fields?.metadata?.name;
    const purchaseIntensionField = fields?.fields?.find(field => field?.model?.fieldTypeItemId === customFieldTypes.purchaseIntentionFieldTypeItemId);
    const purchaseIntensionValueFieldName = (purchaseIntensionField as any)?.valueField?.name;
    const purchaseIntensionValue = purchaseIntensionValueFieldName && formData.get()?.find(data => data.key === purchaseIntensionValueFieldName)?.value;
    const purchaseIntensionText = purchaseIntensionField?.model?.items.find(item => item?.value === purchaseIntensionValue)?.text;
    const contactReasonField = fields?.fields?.find(field => field?.model?.fieldTypeItemId === customFieldTypes.contactReasonFieldTypeItemId);
    const contactReasonValueFieldName = (contactReasonField as any)?.valueField?.name;
    const contactReasonValue = contactReasonValueFieldName && formData.get()?.find(data => data.key === contactReasonValueFieldName)?.value;
    const contactReasonText = contactReasonField?.model?.items.find(item => item?.value === contactReasonValue)?.text;

    const productSelectorField = fields?.fields?.find(
      (field) => field?.model?.fieldTypeItemId === customFieldTypes.productSelectorFieldTypeItemId
    );
    const productItems = () => {
      if (productSelectorField) {
        return productSelectorItems.map((obj) => ({
          item_id: obj?.modelsId,
          item_name: obj?.modelsTitle,
          product_line: obj?.lineTitle,
          product_series: obj?.seriesTitle,
          product_model: obj?.modelsTitle,
        }));
      }
    };
    

    googleDataLayerPush({
      event: 'form_submit',
      form_name: formNameValue,
      PurchaseIntention: purchaseIntensionText,
      ContactReason: contactReasonText,
      items: productItems(),
    });
  }

  const successResponseHandler = (res, formData) => {
    if (res?.success) {
      if(res?.errors.length === 0){
        pushDataToAnalytics(formData);
      }
      updateShowThankyouMessage(true);
    }
    return res;
  }

  const fetchData = async () => {
    const getApiURL = `/${APIPrefix}/thankyoumessage/message/${thankyouMessageId}/${language}`;
    try {
      const res = await fetch(getApiURL);
      const result = await res.json();
      updateThankYouMessageFields({...result});
    } catch (error) {
      console.error(`${error} occurred while fetching data`);
    }
  };

  useEffect(() => {
    thankyouMessageId && fetchData();
  }, []);

  return (
    <>
      {
        (showThankyouMessage && thankYouMessageFields) ?
          <ThankYou
            redirectionLink={thankYouMessageFields?.redirectPageLink}
            title={thankYouMessageFields?.thankyouHeading}
            message={thankYouMessageFields?.thankyouMessage}
          /> :
          (<section className="sitecore-jss-form">
             <CustomFormContextProvider sitecoreForm={fields} params={params} setProductSelector={(value) => setProductSelectorItems(value)}>
              <Form
                form={fields}
                language={language || defaultLanguage}
                sitecoreApiHost={sitecoreApiHost}
                sitecoreApiKey={sitecoreApiKey}
                fieldFactory={fieldFactory}
                errorComponent={ErrorComponent}
                fieldValidationErrorsComponent={FieldValidationError}
                fieldWrapperComponent={fieldWrapper}
                formFetcher={(formData: JssFormData, endpoint: string) => {
                  windowService().dispatchEvent?.(new CustomEvent(EventEnum.ADD_LOADER_QUEUE_ITEM));
                  return fetch(endpoint, {
                    body: formData.toMultipartFormData(),
                    method: 'post',
                    credentials: 'include',
                  })
                    .then((res) => { return res.json() })
                    .then((res) => successResponseHandler(res as FormResult, formData))
                    .finally(() => windowService().dispatchEvent?.(new CustomEvent(EventEnum.REMOVE_LOADER_QUEUE_ITEM)));
                }}
                onRedirect={(url): void => {
                  if (isInternalUrl(url)) {
                    router.navigate(url);
                  } else {
                    windowService().location.href = url;
                  }
                }}
              />
            </CustomFormContextProvider>
          </section>)
      }
    </>
  );
};

export default withRouter(SitecoreJssForm);
